import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  HStack,
  ModalBodyProps,
} from "@chakra-ui/react";
import { ReactNode, useCallback, useRef, useState } from "react";
import { useDraggable } from "src/hooks/useDraggable";
import { useAutosavedState } from "src/hooks/useLocalStorage";
import { z } from "zod";

export type LegacyResizableDrawerProps = {
  autosaveId?: string;
  header: ReactNode;
  footer?: ReactNode;
  showCloseButton?: boolean;
  onCloseButtonClick?: () => void;
  contentProps?: ResizableDrawerContentProps;
  bodyProps?: ModalBodyProps;
} & DrawerProps;

export function LegacyResizableDrawer({
  children,
  autosaveId,
  header,
  footer,
  showCloseButton = true,
  onCloseButtonClick,
  contentProps,
  bodyProps,
  ...props
}: LegacyResizableDrawerProps) {
  const [bodyRef, hasOverflow] = useHasOverflow<HTMLDivElement>();
  return (
    <Drawer {...props}>
      <DrawerOverlay />
      <ResizableDrawerContent
        bg="white"
        padding="0"
        gap="0"
        autosaveId={autosaveId}
        {...contentProps}
      >
        <DrawerHeader>
          <HStack
            justify="space-between"
            align="top"
            paddingX="6"
            paddingTop="4"
            paddingBottom="2"
            fontSize="xl"
            fontWeight="bold"
          >
            <Box>{header}</Box>
            {showCloseButton && (
              <DrawerCloseButton onClick={onCloseButtonClick} />
            )}
          </HStack>
        </DrawerHeader>
        <DrawerBody
          paddingX="6"
          ref={bodyRef}
          borderTop={hasOverflow ? "1px solid" : "none"}
          borderBottom={hasOverflow ? "1px solid" : "none"}
          borderColor="gray.300"
          {...bodyProps}
        >
          {children}
        </DrawerBody>
        {footer && (
          <DrawerFooter p="4" display="flex" gap="2">
            {footer}
          </DrawerFooter>
        )}
      </ResizableDrawerContent>
    </Drawer>
  );
}

type ResizableDrawerContentProps = {
  autosaveId?: string;
  minWidthPercent?: number;
  maxWidthPercent?: number;
  initialWidthPercent?: number;
} & DrawerContentProps;

// Note: This assumes that the drawer comes from the right side of the screen,
// but that can be changed if needed.
export function ResizableDrawerContent({
  children,
  autosaveId,
  minWidthPercent = 20,
  maxWidthPercent = 90,
  initialWidthPercent = 30,
  ...props
}: ResizableDrawerContentProps) {
  const [width, setWidth] = useAutosavedState({
    key: autosaveId,
    schema: z
      .number()
      .transform((value) => clamp(value, minWidthPercent, maxWidthPercent)),
    initialValue: initialWidthPercent,
  });
  const { isDragging, onMouseDown } = useDraggable({
    onDragStart: useCallback(
      () =>
        ({ current }) => {
          const widthPercent = 100 - (current.x / window.innerWidth) * 100;
          setWidth(clamp(widthPercent, minWidthPercent, maxWidthPercent));
        },
      [minWidthPercent, maxWidthPercent, setWidth]
    ),
  });
  return (
    <DrawerContent {...props} maxWidth={`${width}%`}>
      <Box
        width="4px"
        height="100%"
        position="absolute"
        left={0}
        top={0}
        cursor="col-resize"
        bg={isDragging ? "gray.400" : undefined}
        _hover={{ bg: "gray.400" }}
        onMouseDown={onMouseDown}
      />
      {children}
    </DrawerContent>
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}

function useHasOverflow<T extends HTMLElement>(): [
  (node: T | null) => void,
  boolean
] {
  const ref = useRef<T | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const setRef = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    ref.current = node;
    if (!node) return;

    const check = () => setHasOverflow(node.scrollHeight > node.clientHeight);

    const observer = new ResizeObserver(check);
    observer.observe(node);
    for (const child of Array.from(node.children)) {
      observer.observe(child);
    }
    observerRef.current = observer;
    check();
  }, []);

  return [setRef, hasOverflow];
}
